import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem
} from '@mui/material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { createAdvanceSalary } from './advanceSalaryApi';

const AdvanceSalaryForm = () => {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [form, setForm] = useState({
    branchId: '',
    employeeId: '',
    advanceAmount: '',
    months: '',
    startMonth: ''
  });

  const emi =
    form.advanceAmount && form.months
      ? (form.advanceAmount / form.months).toFixed(2)
      : '';

  // Load branches once
  useEffect(() => {
    api.get('/branches')
      .then(res => setBranches(res.data))
      .catch(() => toast.error('Failed to load branches'));
  }, []);

  // Load employees only for the selected branch (avoids mixing all branches together)
  useEffect(() => {
    if (!form.branchId) {
      setEmployees([]);
      return;
    }
    setLoadingEmployees(true);
    api.get(`/employees/branch/${form.branchId}`)
      .then(res => setEmployees(res.data))
      .catch(() => toast.error('Failed to load employees for this branch'))
      .finally(() => setLoadingEmployees(false));
  }, [form.branchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      // reset employee whenever branch changes so a stale employee from a
      // different branch can never stay selected
      ...(name === 'branchId' ? { employeeId: '' } : {})
    }));
  };

  const handleSubmit = async () => {
    if (!form.branchId) {
      toast.error('Please select a branch first');
      return;
    }
    if (!form.employeeId || !form.advanceAmount || !form.months) {
      toast.error('Fill all required fields');
      return;
    }

    try {
      await createAdvanceSalary({
        employeeId: Number(form.employeeId),
        amount: Number(form.advanceAmount),
        months: Number(form.months)
      });

      toast.success('Advance salary approved');

      setForm({
        branchId: form.branchId, // keep the branch selected for the next entry
        employeeId: '',
        advanceAmount: '',
        months: '',
        startMonth: ''
      });

    } catch (err) {
      toast.error('Failed to save advance salary');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={800} mb={3}>
        Advance Salary Approval
      </Typography>

      <Paper sx={{ p: 4, maxWidth: 600 }}>
        <Grid container spacing={3}>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Branch"
              name="branchId"
              value={form.branchId}
              onChange={handleChange}
            >
              {branches.map(b => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Employee"
              name="employeeId"
              value={form.employeeId}
              onChange={handleChange}
              disabled={!form.branchId || loadingEmployees}
              helperText={!form.branchId ? 'Select a branch first' : ''}
            >
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Advance Amount"
              name="advanceAmount"
              type="number"
              value={form.advanceAmount}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Number of Months"
              name="months"
              type="number"
              value={form.months}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Monthly EMI"
              value={emi}
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="month"
              label="Deduction Start Month (UI only)"
              name="startMonth"
              value={form.startMonth}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
            >
              Approve Advance Salary
            </Button>
          </Grid>

        </Grid>
      </Paper>
    </Box>
  );
};

export default AdvanceSalaryForm;